import AddIcon from '@mui/icons-material/Add';
import { Box, Paper, Popover, Typography } from '@mui/material';
import { ActionButton } from '@core/components';
import { usePreloadedImages } from '@core/hooks/usePreloadedImages';
import type { Order } from '../../../api/order';
import { getFullName } from '../../utils/customer';
import { VESTMENT_CATEGORY_IMAGE_URLS } from '../../utils/vestment';
import OrderItemDialog from '../OrderForm/OrderItemDialog';
import VestmentCategoryPicker from '../OrderForm/VestmentCategoryPicker';
import OrderDetailItem from './OrderDetailItem';
import type { OrderUpdatedHandler } from './useOrderDetailHandlers';
import { useOrderItemActions } from './useOrderItemActions';

interface OrderItemsCardProps {
  order: Order;
  onUpdated?: OrderUpdatedHandler;
}

const OrderItemsCard = ({ order, onUpdated }: OrderItemsCardProps) => {
  const {
    readOnly,
    addMenuAnchor,
    isAddMenuOpen,
    openAddMenu,
    closeAddMenu,
    openAddItemDialog,
    itemDialog,
    closeItemDialog,
    openEditItemDialog,
    handleDuplicateItem,
    handleDeleteItem,
    handleDialogSubmit,
  } = useOrderItemActions(order, onUpdated);

  usePreloadedImages(readOnly ? [] : VESTMENT_CATEGORY_IMAGE_URLS, {
    latch: true,
  });

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="caption" color="text.secondary">
            {!readOnly && order.items.length === 0
              ? `What are you making for ${getFullName(order.customer)}?`
              : `Items (${order.items.length})`}
          </Typography>
          {!readOnly && order.items.length > 0 ? (
            <>
              <ActionButton
                variant="primary"
                size="xs"
                onClick={openAddMenu}
                icon={<AddIcon />}
              >
                Add item
              </ActionButton>
              <Popover
                anchorEl={addMenuAnchor}
                open={isAddMenuOpen}
                onClose={closeAddMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ p: 2, width: { xs: 320, sm: 640 } }}>
                  <VestmentCategoryPicker onSelect={openAddItemDialog} />
                </Box>
              </Popover>
            </>
          ) : null}
        </Box>
        <Box display="flex" flexDirection="column" gap={1.5}>
          {order.items.length > 0 ? (
            order.items.map((item) => (
              <OrderDetailItem
                key={item.id}
                item={item}
                orderStatus={order.status}
                readOnly={readOnly}
                onUpdated={onUpdated}
                onEdit={() => openEditItemDialog(item)}
                onDuplicate={() => handleDuplicateItem(item)}
                onDelete={() => handleDeleteItem(item)}
              />
            ))
          ) : !readOnly ? (
            <VestmentCategoryPicker onSelect={openAddItemDialog} />
          ) : null}
        </Box>
      </Paper>

      {itemDialog ? (
        <OrderItemDialog
          open
          variant={itemDialog.itemId === null ? 'add' : 'edit'}
          initialValues={itemDialog.initialValues}
          expandedSection={itemDialog.expandedSection}
          orderId={order.id}
          orderCustomerId={order.customer.id}
          onSubmit={handleDialogSubmit}
          onClose={closeItemDialog}
        />
      ) : null}
    </>
  );
};

export default OrderItemsCard;
